// src/layouts/AppLayout.jsx — Sidebar + TopBar + main content wrapper
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import {
  PageLoadingBoundary,
  PagePresentationProvider,
} from '../context/PagePresentationContext';
import { STORAGE_KEYS } from '../config/env';
import { breadcrumbForPath } from '../config/routes';

const readCollapsed = () => {
  try { return localStorage.getItem(STORAGE_KEYS.sidebar) === 'collapsed'; } catch { return false; }
};

export default function AppLayout({ children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const breadcrumbs = breadcrumbForPath(location.pathname);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      collapsed ? 'var(--sidebar-rail-width)' : '292px',
    );
  }, [collapsed]);

  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEYS.sidebar, next ? 'collapsed' : 'expanded'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return (
    <>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
      />
      <Drawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
        }}
      >
        <Sidebar mobile onNavigate={() => setMobileNavOpen(false)} />
      </Drawer>
      <main className="main">
        <PagePresentationProvider key={location.pathname}>
          <TopBar
            breadcrumbs={breadcrumbs}
            onOpenNavigation={() => setMobileNavOpen(true)}
          />
          <PageLoadingBoundary label={t('common.loadingPage', { defaultValue: 'Loading page' })}>
            <div className="fade-slide-up">
              {children}
            </div>
          </PageLoadingBoundary>
        </PagePresentationProvider>
      </main>
    </>
  );
}
